import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col } from 'components/Grid/Grid';
import { Colors } from 'core/CssVariables';
import { useAppDispatch, useAppSelector } from 'redux/store';
import { removeStoredFiltersByPath, setStoredFilters } from 'redux/slices';
import {
  GaugesEnum,
  TGaugeContent,
  TGaugeMetaDataUi,
  TGaugesUi,
} from 'data/types/gauges.types';
import {
  AllTicketsPath,
  ETicketSpecialFlagsUi,
  TicketFilters,
} from 'data/types/ticket.types';
import { AllTasksPath, TaskFilters } from 'data/types/task.type';
import { TQuickFiltersStorageNames } from 'helpers/storage';
import { AllCompaniesPath } from 'data/types/company.types';
import { CompanyFilters } from 'pages/private/Customers/Organizations/AllCompanies/AllCompanies.type';
import useGetGauges from 'api/hooks/useGetGauges';
import { useGetGaugesMutation } from 'redux/apiSlices/workFeed.slice';
import Can from 'components/Can/Can';
import { PermissionNames } from 'components/Can/Permissions/Permissions.type';
import GaugeItem, { TGaugeSegment } from './components/GaugeItem';
import { SGaugesRow } from './Gauges.style';
import GaugeReset from './components/GaugeReset';

type TGaugesProps = {
  // When true (the header dashbar) the gauges are laid out on a single,
  // non-wrapping row that shrinks each gauge to fit the available width. When
  // omitted (the mobile dropdown) they keep the default wrapping grid.
  fitOneRow?: boolean;
  // When true, force the compact icon-only rendering regardless of the header
  // expand/collapse setting (used on narrower laptops where the labelled row
  // no longer fits — see Header).
  forceCollapsed?: boolean;
};

const Gauges = ({ fitOneRow, forceCollapsed }: TGaugesProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [gauges, setGauges] = useState<TGaugesUi[] | undefined | null>([]);
  const user = useAppSelector(state => state.authSlice.user);
  const [getGauges, { data: gaugesData, isLoading: getGaugesLoading }] =
    useGetGaugesMutation();
  const { data, refetch } = useGetGauges();

  useEffect(() => {
    setGauges(data);
  }, [data]);

  useEffect(() => {
    if (gaugesData) setGauges(gaugesData);
  }, [gaugesData]);

  useEffect(() => {
    if (user?.organization?.id) {
      refetch(user?.organization?.id || '');
    }
  }, [user]);
  const redirectOverdueTicket = () => {
    dispatch(
      removeStoredFiltersByPath({
        keyStorageName: TQuickFiltersStorageNames.ticketFilterStorageName,
        path: AllTicketsPath.OPEN,
      }),
    );
    dispatch(
      setStoredFilters({
        keyStorageName: TQuickFiltersStorageNames.ticketFilterStorageName,
        path: AllTicketsPath.OPEN,
        filter: TicketFilters.SPECIAL_FLAGS,
        ids: [ETicketSpecialFlagsUi.IS_OVERDUE],
      }),
    );
    navigate('/app/tickets?tab=open');
  };

  const redirectOverdueTask = () => {
    dispatch(
      removeStoredFiltersByPath({
        keyStorageName: TQuickFiltersStorageNames.taskFilterStorageName,
        path: AllTasksPath.OPEN,
      }),
    );
    dispatch(
      setStoredFilters({
        keyStorageName: TQuickFiltersStorageNames.taskFilterStorageName,
        path: AllTasksPath.OPEN,
        filter: TaskFilters.OVERDUE,
        ids: ['Overdue'],
      }),
    );
    navigate('/app/tasks?tab=open');
  };

  const redirectNewTickets = () => {
    dispatch(
      removeStoredFiltersByPath({
        keyStorageName: TQuickFiltersStorageNames.ticketFilterStorageName,
        path: AllTicketsPath.UNASSIGNED,
      }),
    );
    navigate('/app/tickets?tab=unassigned&&sort=firstResponseSla,DESC');
  };

  const redirectSla = () => {
    dispatch(
      removeStoredFiltersByPath({
        keyStorageName: TQuickFiltersStorageNames.ticketFilterStorageName,
        path: AllTicketsPath.ALL,
      }),
    );
    dispatch(
      setStoredFilters({
        keyStorageName: TQuickFiltersStorageNames.ticketFilterStorageName,
        path: AllTicketsPath.ALL,
        filter: TicketFilters.SPECIAL_FLAGS,
        ids: [ETicketSpecialFlagsUi.SLA_CLOSE_TO_FAIL],
      }),
    );
    navigate('/app/tickets?tab=all');
  };

  const redirectOpenTickets = () => {
    dispatch(
      removeStoredFiltersByPath({
        keyStorageName: TQuickFiltersStorageNames.ticketFilterStorageName,
        path: AllTicketsPath.OPEN,
      }),
    );
    navigate('/app/tickets?tab=open&&sort=firstResponseSla,DESC');
  };
  const redirectAvgArgency = () => {
    dispatch(
      removeStoredFiltersByPath({
        keyStorageName: TQuickFiltersStorageNames.ticketFilterStorageName,
        path: AllTicketsPath.OPEN,
      }),
    );
    navigate('/app/tickets?tab=open&&sort=score,DESC');
  };
  const redirectCustomers = () => {
    dispatch(
      setStoredFilters({
        keyStorageName: TQuickFiltersStorageNames.companyStorageName,
        path: AllCompaniesPath.CUSTOMERS,
        filter: CompanyFilters.SPECIAL_FLAGS,
        ids: [
          'NEEDS_INTERACTION',
          'NEEDS_SCHEDULED_INTERACTION',
          'BLOCK_HOURS_LOW',
          'BLOCK_HOURS_DEPLETED',
          'BLOCK_HOURS_EXPIRED',
        ],
      }),
    );
    navigate('/app/customers/companies?tab=customers');
  };

  const handleItemClick = (type: GaugesEnum) => {
    switch (type) {
      case GaugesEnum.NEW_TICKETS:
        return redirectNewTickets;
      case GaugesEnum.SLA_CLOSE_TO_FAIL:
        return redirectSla;
      case GaugesEnum.OVERDUE_TICKETS:
        return redirectOverdueTicket;
      case GaugesEnum.OVERDUE_TASKS:
        return redirectOverdueTask;
      case GaugesEnum.OPEN_TICKETS:
        return redirectOpenTickets;
      case GaugesEnum.CUSTOMERS_NEEDING_INTERACTION:
        return redirectCustomers;
      case GaugesEnum.AVG_URGENCY:
        return redirectAvgArgency;
      default:
        return undefined;
    }
  };

  /* What a tile needs to render. `segments` is present only on the merged
     ACTIVE TICKETS tile, which is why it is optional here rather than cast at
     the point of use. */
  type TDashbarGauge = {
    tooltipData: TGaugeMetaDataUi[];
    color?: Colors;
    name: string;
    description: string;
    subLabel?: TGaugeContent['subLabel'];
    type: GaugesEnum;
    onClick?: () => void;
    segments?: TGaugeSegment[];
  };

  const gaugeData = useMemo<TDashbarGauge[] | undefined>(() => {
    return gauges?.map((gauge: TGaugesUi) => {
      return {
        tooltipData: gauge.metaData,
        color: gauge.color,
        name: gauge.gaugeContent.name,
        description: gauge.gaugeContent.description,
        subLabel: gauge.gaugeContent.subLabel,
        type: gauge.type,
        ...(handleItemClick(gauge.type) && {
          onClick: handleItemClick(gauge.type),
        }),
      };
    });
  }, [gauges]);

  /* ZEST-757: the design shows one ACTIVE TICKETS gauge reading
     "<open> Total Open • <new> New" in place of the separate Open and New
     tiles. Both halves stay clickable and keep the drill-down they have today,
     and the New half keeps its own severity colour so a failed first-response
     SLA still shows as red rather than becoming plain text. If either gauge is
     missing the row falls back to whatever the backend sent. */
  const dashbarGauges = useMemo<TDashbarGauge[] | undefined>(() => {
    if (!gaugeData) return gaugeData;
    const open = gaugeData.find(g => g.type === GaugesEnum.OPEN_TICKETS);
    const newTickets = gaugeData.find(g => g.type === GaugesEnum.NEW_TICKETS);
    if (!open || !newTickets) return gaugeData;

    /* The bar shows the tile's state, so it has to take the worse of the two
       halves — otherwise a red New count sits under a calm blue bar and the bar
       says the opposite of the number above it. */
    const severityRank = (c?: Colors) => {
      if (c === Colors.ErrorColor) return 3;
      if (c === Colors.WarningColor) return 2;
      if (c === Colors.SuccessColor) return 1;
      return 0;
    };
    const worstColor =
      severityRank(newTickets.color) > severityRank(open.color)
        ? newTickets.color
        : open.color;

    const merged = {
      ...open,
      name: 'Active Tickets',
      color: worstColor,
      onClick: undefined,
      segments: [
        {
          label: 'Total Open',
          value: open.tooltipData[0]?.value,
          color: open.color,
          onClick: open.onClick,
          tooltipData: open.tooltipData,
        },
        {
          label: 'New',
          value: newTickets.tooltipData[0]?.value,
          color: newTickets.color,
          onClick: newTickets.onClick,
          tooltipData: newTickets.tooltipData,
        },
      ],
      tooltipData: [...open.tooltipData, ...newTickets.tooltipData],
    };

    return gaugeData
      .filter(g => g.type !== GaugesEnum.NEW_TICKETS)
      .map(g => (g.type === GaugesEnum.OPEN_TICKETS ? merged : g));
  }, [gaugeData]);
  const onRefresh = async () => {
    await getGauges().unwrap();
  };
  useEffect(() => {
    // get gauges from BE, when there aren`t gauges collection in firebase yet
    if (!!data && !data.length) {
      onRefresh();
    }
  }, [data]);
  return (
    <SGaugesRow
      id="dashbar_gauges"
      /* design: the gauge row's itemSpacing is 24 */
      /* No `align`: antd 4.18.9 has no CSS for align="stretch", and the flex
         default already stretches tiles to equal height, which is what puts
         every severity bar on one baseline. */
      gutter={[24, 12]}
      wrap={!fitOneRow}
      $fitOneRow={fitOneRow}
    >
      {dashbarGauges?.map(gauge => (
        <React.Fragment key={gauge.type}>
          <Col {...(fitOneRow && { flex: '0 1 auto' })}>
            <GaugeItem
              onClick={gauge.onClick}
              tooltipData={gauge.tooltipData}
              backgroundColor={gauge?.color}
              type={gauge.type}
              name={gauge.name}
              subLabel={gauge.subLabel}
              segments={gauge.segments}
              description={gauge.description}
              fitOneRow={fitOneRow}
              forceCollapsed={forceCollapsed}
            />
          </Col>
        </React.Fragment>
      ))}
      <Can permission={PermissionNames.RESET_GAUGE_DATA}>
        {dashbarGauges?.length && (
          <GaugeReset isLoading={getGaugesLoading} onRefresh={onRefresh} />
        )}
      </Can>
    </SGaugesRow>
  );
};

export default Gauges;
